import IPaciente from "../types/Paciente";
import moment from 'moment';
import 'moment/dist/locale/es';
import ILog from "../types/Log";
import LogService from "../services/LogService";
import * as constantes from "../common/Constantes";
import jsPDF from 'jspdf';
import IDocumento from "../types/Documento";
import ICita from "../types/Cita";
import AgendaUtils from "./Agenda";
import ConfigUtils from "./Config";
import Utilities from "../common/Utilities";
import { resources } from "../common/Resources";
import IConsentimientoDoc from "../types/CI";
import { Prompt_Regular } from "../fonts/Prompt-Regular";
import { Prompt_Medium } from "../fonts/Prompt-Medium";
import { Prompt_SemiBold } from "../fonts/Prompt-SemiBold";
import { formatCurrency } from "./Factura";

moment.locale(constantes.momentLocale);

const addImageSafe = (
    doc: jsPDF,
    imageDataUrl: string | undefined | null,
    x: number,
    y: number,
    width: number,
    height: number,
    context: string
) => {
    if (!imageDataUrl) return;
 
    try {
        const match = imageDataUrl.match(/^data:image\/(png|jpe?g);base64,/i);
        const format = match ? match[1].toUpperCase().replace("JPG", "JPEG") : "PNG";
 
        doc.addImage(imageDataUrl, format, x, y, width, height);
    } catch (e: any) {
        const error: ILog = {
            method: `Utils.Doc.addImageSafe.${context}`,
            level: 'error',
            message: e.message,
            meta: e.message
        };
        LogService.logError(error);
    }
};

const formatCI = (paciente: IPaciente, signature: any, esMenor: boolean, email: boolean, whatsApp: boolean, CI: IConsentimientoDoc | null, clinicDomicilio: string, clinicTelefono: string, clinicEmail: string, clinicName: string, clinicCity: string): ArrayBuffer => {
    try {

        const doc = new jsPDF('p', 'mm', 'a4');
        const logo = ConfigUtils.getClinicLogo();
        const pais = ConfigUtils.getPais() || 'ES';
        if (logo) {
            addImageSafe(doc, logo, 170, 15, 15, 15, 'logo');
        }

        doc.addFileToVFS("Prompt-Regular.ttf", Prompt_Regular);
        doc.addFont("Prompt-Regular.ttf", "Prompt", "normal");
        doc.addFileToVFS("Prompt-Medium.ttf", Prompt_Medium);
        doc.addFont("Prompt-Medium.ttf", "Prompt", "medium");
        doc.addFileToVFS("Prompt-SemiBold.ttf", Prompt_SemiBold);
        doc.addFont("Prompt-SemiBold.ttf", "Prompt", "semibold");

        doc.setFont('Prompt', 'semibold');
        doc.setFontSize(22);
        doc.text('Protección de datos', 21.5, 28.5, {maxWidth: 150});

        doc.setFont('Prompt', 'normal');
        doc.setFontSize(11);
        doc.text(resources.CI.title, 21.5, 42, {maxWidth: 160});

        doc.setFontSize(9);

        doc.setFont('Prompt', 'medium');
        doc.text(resources.CI.responsable, 21.5,52, {maxWidth: 165});
        doc.setFont('Prompt', 'normal');
        doc.text(resources.CI.responsableTexto
            .replace("{0}", CI?.responsable != undefined ? CI.responsable : '')
            .replace("{1}", CI?.responsableNIF != undefined ? CI.responsableNIF : '')
            .replace("{2}", clinicDomicilio)
            .replace("{3}", clinicTelefono)
            .replace("{4}", clinicEmail)
            .replace("{7}", CI?.responsableNIF != undefined ? Utilities.getIdLabel(CI.responsableNIF, pais) : '')
        , 21.5,57, {maxWidth: 165});
        doc.setFont('Prompt', 'medium');
        doc.text(resources.CI.finalidad, 21.5,67, {maxWidth: 165});
        doc.setFont('Prompt', 'normal');
        doc.text(resources.CI.finalidadTexto, 21.5,72, {maxWidth: 165});

        doc.setFont('Prompt', 'medium');
        doc.text(resources.CI.datos, 21.5,90, {maxWidth: 165});
        doc.setFont('Prompt', 'normal');
        doc.text(resources.CI.datosTexto, 21.5,95, {maxWidth: 165});

        doc.setFont('Prompt', 'medium');
        doc.text(resources.CI.acceso, 21.5,105, {maxWidth: 165});
        doc.setFont('Prompt', 'normal');
        doc.text(resources.CI.accesoTexto.replace("{5}", clinicName), 21.5,110, {maxWidth: 165});

        doc.setFont('Prompt', 'medium');
        doc.text(resources.CI.legitimacion, 21.5,124, {maxWidth: 165});
        doc.setFont('Prompt', 'normal');
        doc.text(resources.CI.legitimacionTexto, 21.5,129, {maxWidth: 165});

        doc.setFont('Prompt', 'medium');
        doc.text(resources.CI.destinatarios, 21.5,135, {maxWidth: 165});
        doc.setFont('Prompt', 'normal');
        doc.text(resources.CI.destinatariosTexto, 21.5,140, {maxWidth: 165});

        doc.setFont('Prompt', 'medium');
        doc.text(resources.CI.plazo, 21.5,150, {maxWidth: 165});
        doc.setFont('Prompt', 'normal');
        doc.text(resources.CI.plazoTexto, 21.5,155, {maxWidth: 165});

        doc.setFont('Prompt', 'medium');
        doc.text(resources.CI.derechos, 21.5,165, {maxWidth: 165});
        doc.setFont('Prompt', 'normal');
        doc.text(resources.CI.derechosTexto, 21.5,170, {maxWidth: 165});

        doc.setFont('Prompt', 'medium');
        doc.text(resources.CI.seguridad, 21.5,184, {maxWidth: 165});
        doc.setFont('Prompt', 'normal');
        doc.text(resources.CI.seguridadTexto, 21.5,189, {maxWidth: 165});

        doc.setFont('Prompt', 'medium');
        doc.text(resources.CI.autorizacion, 21.5,199, {maxWidth: 165});
        
        doc.setFont('Prompt', 'normal');
        doc.rect(21.5, 202, 3, 3);
        doc.line(21.5, 202, 24.5, 205);
        doc.line(21.5, 205, 24.5, 202);
        doc.text(resources.CI.authIncorporacion, 26, 204.5, {maxWidth: 165});

        doc.rect(21.5, 207.5, 3, 3);
        doc.line(21.5, 207.5, 24.5, 210.5);
        doc.line(21.5, 210.5, 24.5, 207.5);
        doc.text(resources.CI.authConfirmacion, 26, 210, {maxWidth: 165});

        if (email) {
            doc.rect(21.5, 213, 3, 3);
            doc.line(21.5, 213, 24.5, 216);
            doc.line(21.5, 216, 24.5, 213);
            doc.text(resources.CI.authEmail, 26, 215.5, {maxWidth: 165});
        } else {
            doc.rect(21.5, 213, 3, 3);
            doc.text(resources.CI.authEmail, 26, 215.5, {maxWidth: 165});
        }

        if (whatsApp) {
            doc.rect(21.5, 218.5, 3, 3);
            doc.line(21.5, 218.5, 24.5, 221.5);
            doc.line(21.5, 221.5, 24.5, 218.5);
            doc.text(resources.CI.authWhatsApp, 26, 221, {maxWidth: 165});
        } else {
            doc.rect(21.5, 218.5, 3, 3);
            doc.text(resources.CI.authWhatsApp, 26, 221, {maxWidth: 165});
        }

        doc.setFont('Prompt', 'medium');
        doc.text(resources.CI.tratamiento, 21.5,228, {maxWidth: 165});
        doc.setFont('Prompt', 'normal');
        doc.text(resources.CI.tratamientoTexto.replace("{6}", CI?.texto != undefined ? CI.texto : ''), 21.5,233, {maxWidth: 165});

        //En función de las líneas de tratamientoTexto
        const height = doc.getTextDimensions(resources.CI.tratamientoTexto.replace("{6}", CI?.texto != undefined ? CI.texto : '')).h;
        const height2 = doc.getTextDimensions(resources.CI.tratamientoTexto.replace("{6}", CI?.texto != undefined ? CI.texto : ''), { fontSize: 9, maxWidth: 165 }).h;

        doc.rect(21.5, 233 + height2, 3, 3);
        doc.line(21.5, 233 + height2, 24.5, 233 + height2 + height);
        doc.line(21.5, 233 + height2 + height, 24.5, 233 + height2);
        doc.text(resources.CI.tratamientoConfirmacion, 26, 233 + height2 + height - 0.5, {maxWidth: 165});

        doc.text(`${clinicCity.toLocaleUpperCase()}, a ${new Date().getDate()} de ${constantes.monthNames[new Date().getMonth()]} de ${new Date().getFullYear()}`, 75,260, {maxWidth: 170});
        doc.text(`Firmado: ${esMenor ? `${paciente.nombreTutor} ${paciente.apellidosTutor}, tutor legal de ${paciente.nombre} ${paciente.apellidos}` : `${paciente.nombre} ${paciente.apellidos}`}`, 21.5,273, {maxWidth: 170});
        doc.text(`${esMenor ? Utilities.getIdLabel(paciente.DNItutor, pais, true) : Utilities.getIdLabel(paciente.DNI, pais)}: ${esMenor ? paciente.DNItutor : paciente.DNI}`, 21.5,278, {maxWidth: 170});
        doc.addImage(signature, 84, 240, 50, 50);
        
        let document = doc.output('arraybuffer');
        return document;

    } catch (e: any) {
        const error: ILog = { method: 'Utils.Doc.formatCI', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
        throw e;
    }
};

const formatJustificanteCita = (paciente: IPaciente, cita: ICita, esMenor: boolean) => {
    try {
        const doc = new jsPDF('l', 'mm', 'a5');
        const logo = ConfigUtils.getClinicLogo();
        if (logo) {
            addImageSafe(doc, logo, 170, 15, 15, 15, 'logo');
        }

        doc.addFileToVFS("Prompt-Regular.ttf", Prompt_Regular);
        doc.addFont("Prompt-Regular.ttf", "Prompt", "normal");
        doc.addFileToVFS("Prompt-SemiBold.ttf", Prompt_SemiBold);
        doc.addFont("Prompt-SemiBold.ttf", "Prompt", "semibold");

        const asistenciaDoc = ConfigUtils.getAsistenciaDoc();
        const pais = ConfigUtils.getPais() || 'ES';
        if (!asistenciaDoc) return;
        doc.setFont('Prompt', 'semibold');
        doc.setFontSize(22);
        doc.text('Justificante de asistencia', 21.5, 25, {maxWidth: 150});
        doc.setFont('Prompt', 'normal');
        doc.setFontSize(9);
        doc.text(`El paciente ${paciente.nombre} ${paciente.apellidos} con ${esMenor ? Utilities.getIdLabel(paciente.DNItutor, pais, true) : Utilities.getIdLabel(paciente.DNI, pais)}: ${esMenor ? `${paciente.DNItutor} (tutor legal)` : paciente.DNI}`, 21.5,45, {maxWidth: 170, align: "justify"});
        doc.text(`Acudió a la clínica ${ConfigUtils.getClinicName()} el ${AgendaUtils.formatCitaDate(cita)} ${AgendaUtils.formatCitaTime(cita)} horas.`, 21.5,50, {maxWidth: 170, align: "justify"});
        doc.text(asistenciaDoc.texto, 21.5,60, {maxWidth: 160, align: "justify"});
        doc.text(`En ${ConfigUtils.getClinicCity()}, a ${new Date().getDate()} de ${constantes.monthNames[new Date().getMonth()]} de ${new Date().getFullYear()}`, 125,87, {maxWidth: 170});
        addImageSafe(doc, asistenciaDoc.sello, 130, 90, 50.45, 32.73, 'sello');
        
        let document = doc.output('datauristring');
        return document;

    } catch (e: any) {
        const error: ILog = { method: 'Utils.Doc.formatJustificanteCita', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
        throw e;
    }
};

// Fecha que figura en la factura: en Borrador la de creación; una vez emitida, la de emisión (fecha de expedición).
const getFechaFactura = (factura: any): { etiqueta: string; fecha: Date } =>
    factura.estado !== "Borrador"
        ? { etiqueta: "Fecha de expedición", fecha: factura.fechaEmision ?? factura.fechaFactura }
        : { etiqueta: "Fecha de creación", fecha: factura.fechaFactura };

const formatFactura = (factura: any, download: boolean, tipoFacturacion: string, detalleCita?: { fecha: string; hora: string }, facturaAsociada?: string, pacienteMenor?: string) => {
    try {
        const doc = new jsPDF('p', 'mm', 'a4');

        const pageHeight = doc.internal.pageSize.getHeight();
        const bottomMargin = 20;

        let y = 25;
        const left = 20;
        const right = 190;
        const monedaFactura = factura.moneda || "EUR";

        // ---------- HELPERS ----------
        const ensurePageSpace = (needed: number) => {
          if (y + needed > pageHeight - bottomMargin) {
            doc.addPage();
            y = 20;
            drawConceptsHeader();
          }
        };

        const drawConceptsHeader = () => {
          doc.setFont("Prompt", "medium");
          doc.setFontSize(9);
          doc.text("Concepto", left, y);
          doc.text("Cantidad", 120, y, { align: "right" });
          doc.text("Precio unit.", 150, y, { align: "right" });
          doc.text("Subtotal", right, y, { align: "right" });

          y += 4;
          doc.setDrawColor(220);
          doc.line(left, y, right, y);
          y += 6;
        };

        const getConceptHeight = (c: any, imp: any) => {
          let h = 5;

          const fechaFactura = new Date(getFechaFactura(factura).fecha).toLocaleDateString("es-ES");

          const pintaDetalleCita =
            !factura.facturaOriginalId &&
            factura.tipo !== "agrupada" &&
            detalleCita?.fecha !== fechaFactura;

          if (pintaDetalleCita) h += 4;
          if (imp) h += 4;

          return h;
        };

        // ---------- FUENTES ----------
        doc.addFileToVFS("Prompt-Regular.ttf", Prompt_Regular);
        doc.addFont("Prompt-Regular.ttf", "Prompt", "normal");
        doc.addFileToVFS("Prompt-Medium.ttf", Prompt_Medium);
        doc.addFont("Prompt-Medium.ttf", "Prompt", "medium");
        doc.addFileToVFS("Prompt-SemiBold.ttf", Prompt_SemiBold);
        doc.addFont("Prompt-SemiBold.ttf", "Prompt", "semibold");

        // ---------- MARCA DE AGUA ----------
        if (factura.estado === "Borrador" || factura.estado === "Anulada") {
          const esAnulada = factura.estado === "Anulada";
          
          doc.setFont("Prompt", "semibold");
          doc.setFontSize(60);
          doc.setTextColor(
              esAnulada ? 250 : 240,
              esAnulada ? 180 : 240,
              esAnulada ? 180 : 240
            );
        
            doc.text(
              esAnulada ? "ANULADA" : "BORRADOR",
              108,
              158,
              {
                align: "center",
                angle: 32,
              }
            );
          doc.setTextColor(0);
        }

        // ---------- CABECERA ----------
        doc.setFont("Prompt", "semibold");
        doc.setFontSize(20);
        doc.text("Factura", left, y);
        
        const logo = ConfigUtils.getClinicLogo();
        if (logo) addImageSafe(doc, logo, 170, 15, 15, 15, 'logo');

        y += 15;

        doc.setFont("Prompt", "medium");
        doc.setFontSize(9);
        doc.text(`Nº: ${factura.numero ?? "Sin número (borrador)"}`, left, y);
        y +=5;
        doc.text(`${getFechaFactura(factura).etiqueta}: ${new Date(getFechaFactura(factura).fecha).toLocaleDateString("es-ES")}`, left, y);
        y +=5;
        if (factura.facturaOriginalId) {
            doc.text(`${factura.tipoRectificacion === "anulacion" ? "Anulación" : "Rectificativa"} de la factura nº ${factura.facturaOriginalId}`, left, y);
            y +=5;
        } else if (factura.facturaRectificadaId) {
            doc.text(`${factura.estado === "Anulada" ? "Anulada" : "Rectificada"} por la factura nº ${factura.facturaRectificada ?? "Sin número (borrador)"}`, left, y);
            y +=5;
        } else if (tipoFacturacion === "parcial") {
            doc.text("Factura parcial", left, y);
            y += 5;
            if (facturaAsociada) {
              doc.text(`Factura complementaria Nº: ${facturaAsociada}`, left, y);
              y += 5;
            }
        }
    
        // ---------- EMISOR ----------
        doc.text("Emisor:", left + 100, 45);
        doc.setFont("Prompt", "normal");
        doc.text(`${factura.emisor.razonSocial}`, left + 105, 45 + 4);
        doc.text(`${factura.emisor.nif}`, left + 105, 45 + 8);
        doc.text(`${factura.emisor.direccion}`, left + 105, 45 + 12);
        doc.text(`${factura.emisor.codigoPostal} ${factura.emisor.ciudad}, España`, left + 105, 45 + 16);
    
        y += 6;

        // ---------- RECEPTOR ----------
        const showPacienteMenor = !Boolean(factura.facturaOriginalId) && Boolean(pacienteMenor);
        doc.setFont("Prompt", "medium");
        doc.text(
          showPacienteMenor ? "Receptor (Tutor legal):" : "Receptor:",
          left,
          y
        );
        doc.setFont("Prompt", "normal");
    
        let yIncrement = 4;

        const drawField = (value: string, x: number, yPos: number) => {
          if (value) {
            doc.text(`${value}`, x, yPos);
            return 4;
          }
          return 0;
        };

        const drawAddress = (address: string, x: number, yPos: number) => {
          if (!address) return 0;
                
          const parts = address
            .split(",")
            .map((p) => p.trim())
            .filter(Boolean);
                
          let increment = 0;
          const lineHeight = 4;
                
          // Detectar dónde empieza el CP
          const postalIndex = parts.findIndex((p) => /^\d{5}\s/.test(p));
                
          let line1 = "";
          let line2 = "";
          let line3 = "";
                
          if (postalIndex !== -1) {
            // Dirección completa
            line1 = parts.slice(0, postalIndex).join(", ");
        
            // CP + ciudad
            line2 = parts[postalIndex];
        
            // Provincia + país
            line3 = parts.slice(postalIndex + 1).join(", ");
          } else {
            // Fallback si no hay CP detectable
            line1 = parts.join(", ");
          }
      
          if (line1) {
            doc.text(line1, x, yPos + increment);
            increment += lineHeight;
          }
      
          if (line2) {
            doc.text(line2, x, yPos + increment);
            increment += lineHeight;
          }
      
          if (line3) {
            doc.text(line3, x, yPos + increment);
            increment += lineHeight;
          }
      
          return increment;
        };

        // Pintamos solo los campos que existen
        yIncrement += drawField(factura.receptor.nombre, left + 5, y + yIncrement);
        if (factura.receptor.nif) yIncrement += drawField(factura.receptor.nif, left + 5, y + yIncrement);
        if (factura.receptor.email)yIncrement += drawField(factura.receptor.email, left + 5, y + yIncrement);
        if (factura.receptor.direccion) yIncrement += drawAddress(factura.receptor.direccion, left + 5, y + yIncrement);
        if (showPacienteMenor) {
            doc.setFont("Prompt", "medium");
            yIncrement += drawField(`Paciente: ${pacienteMenor}`, left, y + yIncrement);
            doc.setFont("Prompt", "normal");
        }

        y += yIncrement + 14;
    
        // ---------- CONCEPTOS ----------
        drawConceptsHeader();
    
        factura.conceptos.forEach((c: any, index: number) => {
          const imp = factura.impuestos?.[index];
          const needed = getConceptHeight(c, imp);

          ensurePageSpace(needed);

          const base = c.cantidad * c.precio;
          const cuota = imp ? base * (imp.cuota / 100) : 0;
        
          doc.text(c.nombre, left, y);
          doc.text(String(c.cantidad), 120, y, { align: "right" });
          doc.text(formatCurrency(parseFloat(c.precio.toString()), monedaFactura), 150, y, { align: "right" });
          doc.text(formatCurrency(base, monedaFactura), right, y, { align: "right" });
          y += 5;
        
          if (!factura.facturaOriginalId) {
            const diaFactura = new Date(getFechaFactura(factura).fecha).toLocaleDateString("es-ES");
            if (detalleCita && detalleCita.fecha !== "" &&  detalleCita.fecha !== diaFactura) {
              doc.setFontSize(8);
              doc.setTextColor(120);
              doc.text(`Servicio realizado el ${detalleCita.fecha}`, left + 5, y);
              doc.setFontSize(9);
              doc.setTextColor(0);
              y += 4;
            }
          }
      
          if (imp) {
            doc.setFontSize(8);
            doc.setTextColor(120);
            doc.text(
              `${imp.tipo} (${imp.cuota}%): ${formatCurrency(cuota, monedaFactura)}`,
              left + 8,
              y
            );
            doc.setFontSize(9);
            doc.setTextColor(0);
            y += 4;
          }
        });

        // ---------- INFO PAGO ----------
        doc.line(left, y, right, y);
        y += 6;

        doc.setFont("Prompt", "medium");
        doc.setFontSize(9);

        if (factura.estadoPago === "Pagada" && factura.metodoPago) {
            doc.text("Método de pago", left, y);
        
            doc.setFont("Prompt", "normal");
            y += 4;
            doc.text(`${factura.metodoPago.nombre}`, left, y);
        
            if (factura.metodoPago.fechaPago) {
                doc.setFontSize(8);
                doc.setTextColor(120);
                y += 4;
                doc.text(
                    `Fecha de pago: ${new Date(factura.metodoPago.fechaPago).toLocaleDateString("es-ES")}`,
                    left,
                    y
                );
                doc.setTextColor(0);
                doc.setFontSize(9);
            }
        
            y += 6;
        } else {
            doc.setFont("Prompt", "normal");
            doc.setFontSize(9);
            doc.text("Estado de pago: Pendiente", left, y);
            y += 6;
        }
    
        // ---------- TOTALES ----------
        ensurePageSpace(35);

        doc.setDrawColor(220);
        doc.line(left, y, right, y);
        y += 6;
    
        let subtotal = 0;
        let ivaTotal = 0;
    
        factura.conceptos.forEach((c: any, i: number) => {
            subtotal += c.precio * c.cantidad;
            const imp = factura.impuestos?.[i];
            if (imp) ivaTotal += c.precio * c.cantidad * (parseFloat(imp.cuota.toString()) / 100);
        });
        
        doc.text(`Subtotal: ${formatCurrency(subtotal, monedaFactura)}`, right, y, { align: "right" });
        y += 5;
        doc.text(`IVA: ${formatCurrency(ivaTotal, monedaFactura)}`, right, y, { align: "right" });
        y += 7;

        doc.setFont("Prompt", "semibold");
        doc.setFontSize(13);
        doc.text(`TOTAL: ${formatCurrency(Number(factura.importeTotal), monedaFactura)}`, right, y, { align: "right" });
    
        if (download) {
            doc.save(factura.numero ? `Factura-${factura.numero}.pdf` : `Factura-BORRADOR-${factura._id}.pdf`);
        } else {
            return doc.output('datauristring');
        }

    } catch (e: any) {
        const error: ILog = { method: 'Utils.Doc.formatFactura', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
        throw e;
    }
};

const formatDoc = async (file: File, pacienteId: string | undefined) => {

    try {

        if (pacienteId === undefined) return null;

        let documento: IDocumento = { nombre: '', tipo: '', documento: null, pacienteId: '' };

        const parts = file.name.split('.');
        const fileExtension = parts.length > 1 ? parts.pop()?.toLowerCase() || '' : '';

        await getBinary(file).then((data) => {
            documento = {
                nombre: file.name,
                tipo: fileExtension,
                documento: data,
                pacienteId: pacienteId
            };
            return documento;
        });

        return documento;

    } catch (e: any) {
        const error: ILog = { method: 'Utils.Doc.formatDoc', level: 'error', message: e.message, meta: e.message };
        LogService.logError(error);
        return null;
    }
};

const getBinary = (file: File): Promise<ArrayBuffer> => {

    const reader = new FileReader();
    reader.readAsArrayBuffer(file);

    return new Promise((resolve, reject) => {
        reader.onload = () => {
            resolve(reader.result as ArrayBuffer);
        }
        reader.onerror = () => {
            reject(reader.error);
        };
    });

};

const DocUtils = {
    formatCI,
    formatDoc,
    formatJustificanteCita,
    formatFactura
};

export default DocUtils;